export const API_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined'
    ? `${window.location.origin}/api`
    : 'https://microeduca.up.railway.app/api');

const AUTH_KEY = 'microeduca_auth';

function getToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? JSON.parse(raw)?.token || null : null;
  } catch {
    return null;
  }
}

async function request(path: string, options: RequestInit = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  // Sessão expirada ou revogada: limpa e manda para o login.
  if (res.status === 401 && path !== '/login') {
    localStorage.removeItem(AUTH_KEY);
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    throw new Error('Sessão expirada');
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();
  // Salvaguarda: garantir array quando a chamada espera lista
  if (options.method === 'GET' && Array.isArray(data) === false && /\/categories$|\/videos$|\/profiles$|\/comments$|\/view-history/.test(path)) {
    return [];
  }
  return data;
}

export const api = {
  // Sessão
  getMe: () => request('/me', { method: 'GET' }),

  // Categories
  getCategories: () => request('/categories', { method: 'GET' }),
  addCategory: (payload: any) => request('/categories', { method: 'POST', body: JSON.stringify(payload) }),
  updateCategory: (id: string, payload: any) => request(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteCategory: (id: string) => request(`/categories/${id}`, { method: 'DELETE' }),

  // Videos
  getVideos: () => request('/videos', { method: 'GET' }),
  addVideo: (payload: any) => request('/videos', { method: 'POST', body: JSON.stringify(payload) }),
  updateVideo: (id: string, payload: any) => request(`/videos/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteVideo: (id: string) => request(`/videos/${id}`, { method: 'DELETE' }),

  // Profiles
  getProfiles: () => request('/profiles', { method: 'GET' }),
  addProfile: (payload: any) => request('/profiles', { method: 'POST', body: JSON.stringify(payload) }),
  updateProfile: (id: string, payload: any) => request(`/profiles/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteProfile: (id: string) => request(`/profiles/${id}`, { method: 'DELETE' }),
  getUserAccess: (id: string) => request(`/profiles/${id}/access`, { method: 'GET' }),
  setUserAccess: (id: string, rules: Array<{ scope_type: 'category' | 'module'; scope_id: string; release_at: string | null }>) =>
    request(`/profiles/${id}/access`, { method: 'PUT', body: JSON.stringify({ rules }) }),
  changePassword: (id: string, currentPassword: string, newPassword: string) =>
    request(`/profiles/${id}/password`, { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),

  // Vimeo
  vimeoAuth: (payload: any) => request('/vimeo-auth', { method: 'POST', body: JSON.stringify(payload) }),
  vimeoCreateUpload: (payload: any, fileSize: number) => request('/vimeo-upload', { method: 'POST', headers: { 'x-file-size': String(fileSize) }, body: JSON.stringify(payload) }),
  vimeoDelete: (videoId: string) => request(`/vimeo/${encodeURIComponent(videoId)}`, { method: 'DELETE' }),
  fixModuleCategorySync: () => request('/videos/fix-module-category-sync', { method: 'POST' }),
  getMentors: () => request('/mentors', { method: 'GET' }),
  getVimeoTokenStatus: () => request('/vimeo-token/status', { method: 'GET' }),
  getVimeoDetails: (videoId: string) => request(`/vimeo-thumbnail/${encodeURIComponent(videoId)}`, { method: 'GET' }),

  // View history
  getViewHistory: (userId?: string) => request(`/view-history${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`, { method: 'GET' }),
  getRecentViews: (limit = 10) => request(`/view-history/recent?limit=${encodeURIComponent(String(limit))}`, { method: 'GET' }),
  addToHistory: (payload: any) => request('/view-history', { method: 'POST', body: JSON.stringify(payload) }),

  // Relatórios gerenciais
  getReport: (nome: 'summary' | 'users' | 'content' | 'categories' | 'timeline' | 'access' | 'access-log', from?: string, to?: string) => {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    const qs = q.toString();
    return request(`/reports/${nome}${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  // Avisos
  getAnnouncements: (todos = false) => request(`/announcements${todos ? '?all=true' : ''}`, { method: 'GET' }),
  addAnnouncement: (payload: unknown) => request('/announcements', { method: 'POST', body: JSON.stringify(payload) }),
  updateAnnouncement: (id: string, payload: unknown) => request(`/announcements/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteAnnouncement: (id: string) => request(`/announcements/${id}`, { method: 'DELETE' }),

  // Mensagens
  getMessages: (userId?: string) => request(`/messages${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`, { method: 'GET' }),
  getMessageThreads: () => request('/messages/threads', { method: 'GET' }),
  getUnreadCount: () => request('/messages/unread', { method: 'GET' }),
  sendMessage: (body: string, userId?: string) =>
    request('/messages', { method: 'POST', body: JSON.stringify({ body, userId }) }),

  // Settings
  getSetting: (key: string) => request(`/settings/${encodeURIComponent(key)}`, { method: 'GET' }),
  setSetting: (key: string, value: any) => request(`/settings/${encodeURIComponent(key)}`, { method: 'POST', body: JSON.stringify(value) }),

  // Files
  uploadFile: (payload: { filename: string; mimeType: string; dataBase64: string }) => request('/files', { method: 'POST', body: JSON.stringify(payload) }),
  deleteFile: (id: string) => request(`/files/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Comments
  getComments: (videoId: string) => request(`/comments?videoId=${encodeURIComponent(videoId)}`, { method: 'GET' }),
  addComment: (payload: any) => request('/comments', { method: 'POST', body: JSON.stringify(payload) }),
  deleteComment: (id: string) => request(`/comments/${id}`, { method: 'DELETE' }),

  // Video progress
  getVideoProgress: (userId: string, videoId: string) => request(`/video-progress?userId=${encodeURIComponent(userId)}&videoId=${encodeURIComponent(videoId)}`, { method: 'GET' }),
  saveVideoProgress: (payload: any) => request('/video-progress', { method: 'POST', body: JSON.stringify(payload) }),

  // Modules
  getModules: (categoryId?: string) => request(`/modules${categoryId ? `?categoryId=${encodeURIComponent(categoryId)}` : ''}`, { method: 'GET' }),
  getModulesFor: (categoryIds: string[]) =>
    request(`/modules${categoryIds.length ? `?categoryIds=${encodeURIComponent(categoryIds.join(','))}` : ''}`, { method: 'GET' }),
  addModule: (payload: any) => request('/modules', { method: 'POST', body: JSON.stringify(payload) }),
  updateModule: (id: string, payload: any) => request(`/modules/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteModule: (id: string) => request(`/modules/${id}`, { method: 'DELETE' }),
};
